import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`The Python SDK provides a Django middleware that automatically wraps all requests with a `}<a parentName="p" {...{
        "href": "/docs/libraries/python#contexts"
      }}>{`context`}</a>{`. This middleware extracts session and user information from request headers and tags all events captured during the request with relevant metadata.`}</p>
    <h3 {...{
      "id": "basic-setup"
    }}>{`Basic setup`}</h3>
    <p>{`Add the middleware to your Django settings:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`MIDDLEWARE = [
    # ... other middleware
    'posthog.integrations.django.PosthogContextMiddleware',
    # ... other middleware
]
`}</code></pre>
    <p>{`The middleware automatically extracts and uses:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Session ID`}</strong>{` from the `}<inlineCode parentName="li">{`X-POSTHOG-SESSION-ID`}</inlineCode>{` header, if present`}</li>
      <li parentName="ul"><strong parentName="li">{`Distinct ID`}</strong>{` from the `}<inlineCode parentName="li">{`X-POSTHOG-DISTINCT-ID`}</inlineCode>{` header, if present`}</li>
      <li parentName="ul"><strong parentName="li">{`Current URL`}</strong>{` as `}<inlineCode parentName="li">{`$current_url`}</inlineCode></li>
      <li parentName="ul"><strong parentName="li">{`Request method`}</strong>{` as `}<inlineCode parentName="li">{`$request_method`}</inlineCode></li>
    </ul>
    <p>{`All events captured during the request (including exceptions) will include these properties and be associated with the extracted session and distinct ID.`}</p>
    <p>{`If you are using PostHog on your frontend, the JavaScript Web SDK will add the session and distinct ID headers automatically if you enable tracing headers.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`__add_tracing_headers: true
`}</code></pre>
    <h3 {...{
      "id": "exception-capture"
    }}>{`Exception capture`}</h3>
    <p>{`By default, the middleware captures exceptions and sends them to PostHog's error tracking. Disable this by setting:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`# settings.py
POSTHOG_MW_CAPTURE_EXCEPTIONS = False
`}</code></pre>
    <h3 {...{
      "id": "adding-custom-tags"
    }}>{`Adding custom tags`}</h3>
    <p>{`Use `}<inlineCode parentName="p">{`POSTHOG_MW_EXTRA_TAGS`}</inlineCode>{` to add custom properties to all requests:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`# settings.py
def add_user_tags(request):
    # type: (HttpRequest) -> Dict[str, Any]
    tags = {}
    if hasattr(request, 'user') and request.user.is_authenticated:
        tags['user_id'] = request.user.id
        tags['email'] = request.user.email
    return tags

POSTHOG_MW_EXTRA_TAGS = add_user_tags
`}</code></pre>
    <h4 {...{
      "id": "filtering-requests"
    }}>{`Filtering requests`}</h4>
    <p>{`Skip tracking for certain requests using `}<inlineCode parentName="p">{`POSTHOG_MW_REQUEST_FILTER`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`# settings.py
def should_track_request(request):
    # type: (HttpRequest) -> bool
    # Don't track health checks or admin requests
    if request.path.startswith('/health') or request.path.startswith('/admin'):
        return False
    return True

POSTHOG_MW_REQUEST_FILTER = should_track_request
`}</code></pre>
    <h3 {...{
      "id": "modifying-default-tags"
    }}>{`Modifying default tags`}</h3>
    <p>{`Use `}<inlineCode parentName="p">{`POSTHOG_MW_TAG_MAP`}</inlineCode>{` to modify or remove default tags:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`# settings.py
def customize_tags(tags):
    # type: (Dict[str, Any]) -> Dict[str, Any]
    # Remove URL for privacy
    tags.pop('$current_url', None)
    # Add custom prefix to method
    if '$request_method' in tags:
        tags['http_method'] = tags.pop('$request_method')
    return tags

POSTHOG_MW_TAG_MAP = customize_tags
`}</code></pre>
    <h3 {...{
      "id": "complete-configuration-example"
    }}>{`Complete configuration example`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`# settings.py
def add_request_context(request):
    # type: (HttpRequest) -> Dict[str, Any]
    tags = {}
    if hasattr(request, 'user') and request.user.is_authenticated:
        tags['user_type'] = 'authenticated'
        tags['user_id'] = str(request.user.id)
    else:
        tags['user_type'] = 'anonymous'

    # Add request info
    tags['user_agent'] = request.META.get('HTTP_USER_AGENT', '')
    return tags

def filter_tracking(request):
    # type: (HttpRequest) -> bool
    # Skip internal endpoints
    return not request.path.startswith(('/health', '/metrics', '/admin'))

def clean_tags(tags):
    # type: (Dict[str, Any]) -> Dict[str, Any]
    # Remove sensitive data
    tags.pop('user_agent', None)
    return tags

POSTHOG_MW_EXTRA_TAGS = add_request_context
POSTHOG_MW_REQUEST_FILTER = filter_tracking
POSTHOG_MW_TAG_MAP = clean_tags
POSTHOG_MW_CAPTURE_EXCEPTIONS = True
`}</code></pre>
    <p>{`All events captured within the request context automatically include the configured tags and are associated with the session and user identified from the request headers.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      